import { useNavigate } from 'react-router-dom'

import { PiX } from 'react-icons/pi'

import { Modal } from 'components/UI/Modal'
import { Alert } from 'components/FbUI/Alert'
import FbButton from 'components/FbUI/FbButton'
import { Ds2026ButtonProvider } from 'components/FbUI/Ds2026ButtonContext'
import ReactSelectWrapper from 'components/FormUtils/ReactSelectWrapper'

import { useGetCampaigns } from './campaigns_api'

interface CreateSequenceFromExistingModalProps {
  show: boolean
  onHide: () => void
  /** The campaign being filled — the wizard the user lands in after picking. */
  currentCampaignId: number
}

interface SourceOption {
  value: number
  label: string
  meta: string
}

/**
 * Source-picker for "create sequence from existing" (FIR-7659), launched from
 * the campaign detail page. Lists campaigns that already have a sequence
 * (excluding the current one); selecting one navigates into this campaign's
 * sequence wizard with the source carried in the URL (`?from=<id>`), where the
 * copy engine hydrates the wizard. The URL param is what makes the prefill
 * survive a reload.
 */
export function CreateSequenceFromExistingModal({
  show,
  onHide,
  currentCampaignId,
}: CreateSequenceFromExistingModalProps) {
  const navigate = useNavigate()

  const { data, isFetching, isError, refetch } = useGetCampaigns(
    undefined,
    !show,
    { has_sequence: true, exclude_ids: [currentCampaignId] }
  )

  const eligible = data?.results ?? []
  const options: SourceOption[] = eligible.map((campaign) => ({
    value: campaign.id,
    label: campaign.name,
    meta: [
      campaign.type?.name,
      campaign.product?.name,
      campaign.workflow_status,
    ]
      .filter(Boolean)
      .join(' · '),
  }))

  const handleSelect = (sourceId: number) => {
    // Navigate first; the modal unmounts with the page transition (no separate
    // close call racing the navigation).
    navigate(`/campaigns/${currentCampaignId}/sequence?from=${sourceId}`)
  }

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Ds2026ButtonProvider>
        <div className="flex items-center justify-between border-b border-line px-6 py-4">
          <div>
            <h2 className="text-lg font-medium text-fg">
              Create sequence from existing
            </h2>
            <p className="mt-1 text-sm text-fg-secondary">
              Copy another campaign&apos;s steps, settings, schedule, and
              senders into this one. Contacts aren&apos;t copied.
            </p>
          </div>
          <button
            type="button"
            onClick={onHide}
            className="flex size-8 items-center justify-center rounded-lg transition-colors hover:bg-surface-hover"
            aria-label="Close create from existing modal"
          >
            <PiX className="size-4 text-fg-muted" />
          </button>
        </div>

        <Modal.Body className="px-6 pb-6 pt-4">
          {isError ? (
            <div className="flex flex-col items-start gap-3">
              <Alert
                color="danger"
                description="Couldn't load campaigns to copy from. Try again."
              />
              <FbButton
                type="button"
                variant="secondary"
                onClick={() => void refetch()}
              >
                Retry
              </FbButton>
            </div>
          ) : !isFetching && options.length === 0 ? (
            <Alert
              color="default"
              description="No campaigns with a sequence to copy from yet. Build one first, or start blank."
            />
          ) : (
            <div>
              <label
                htmlFor="create-from-existing-source"
                className="mb-1 block text-sm font-medium text-fg"
              >
                Copy from campaign
              </label>
              <ReactSelectWrapper
                inputId="create-from-existing-source"
                instanceId="create-from-existing-source"
                menuPosition="fixed"
                isClearable={false}
                isLoading={isFetching}
                options={options}
                placeholder="Select a campaign to copy"
                onChange={(newValue: unknown) => {
                  const option = newValue as SourceOption | null
                  if (option) handleSelect(option.value)
                }}
                formatOptionLabel={(raw: unknown) => {
                  const option = raw as SourceOption
                  return (
                    <div className="flex flex-col">
                      <span className="text-sm text-fg">{option.label}</span>
                      {option.meta && (
                        <span className="text-xs text-fg-tertiary">
                          {option.meta}
                        </span>
                      )}
                    </div>
                  )
                }}
              />
            </div>
          )}
        </Modal.Body>
      </Ds2026ButtonProvider>
    </Modal>
  )
}
